import BaseApi from './BaseApi.js';

/**
 * CalendarApi — Symfony API client for the calendar module.
 *
 * Wraps every org-scoped calendar endpoint exposed by
 * `symphony-holdshort/src/Controller/CalendarController.php`. All org-scoped
 * methods go through `BaseApi.buildOrgEndpoint()` so that the resolved URL is
 * `/api/v1/org/{organizationUnique}/calendar/...` (no explicit `/api/v1/`
 * string anywhere — the base URL already contains it).
 *
 * Consumed by Phase 1 Month View and all later calendar phases (Week, Day,
 * Stacked, Dispatch). NEVER call legacy PHP endpoints (`/calendar/moveEvent/…`,
 * `addMyEvent/…`, etc.) — see CLAUDE.md "USE SYMFONY API FOR NEW PAGES".
 */
class CalendarApi extends BaseApi {
	static get endpoint() {
		return '/calendar';
	}

	/**
	 * Build a query string from a plain params object, dropping
	 * `null`/`undefined` values so they don't end up as `?foo=undefined`.
	 *
	 * @param {Object} [params]
	 * @returns {string} Either an empty string or a leading-`?` query string.
	 */
	static _buildQuery(params) {
      if (!params) return '';
      const search = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
          if (value === null || value === undefined) return;
          if (Array.isArray(value)) {
              // Symfony DTO deserializer expects PHP bracket array syntax
              // (`key[]=v1&key[]=v2`) for `public ?array $foo` params — plain
              // `key=v1&key=v2` is parsed as a scalar string and fails
              // `#[Assert\Type('array')]` with "string given".
              value.forEach(v => {
                  if (v !== null && v !== undefined) {
                      search.append(`${key}[]`, v);
                  }
              });
              return;
          }
          search.append(key, value);
      });
      const qs = search.toString();
      return qs ? `?${qs}` : '';
  }

	// ─── Global helpers (NOT org-scoped) ──────────────────────────────────────

	/**
	 * GET /api/v1/calendar/timezones — global timezone list.
	 * Used by the existing legacy calendar action; preserved as-is.
	 *
	 * @param {string|null} [date]
	 * @param {string|null} [type]
	 */
	static async getTimezones(date = null, type = null) {
		const query = this._buildQuery({ date, type });
		return await this.get(`${this.endpoint}/timezones${query}`);
	}

	/**
	 * GET /api/v1/calendar/organizations — organizations the current user has
	 * `VIEW_CALENDAR` permission on. Multi-org filter dropdowns must source
	 * from here (NOT from `getUserOrganizations()` which returns every org
	 * the user belongs to regardless of permission). See CalendarController
	 * `getOrganizations` → `OrganizationManager::getOrganizationsForCalendar`.
	 */
	static async listOrganizations() {
		return await this.get(`${this.endpoint}/organizations`);
	}

	/**
	 * GET /api/v1/calendar/locations — locations across all of the current
	 * user's calendar-visible orgs. Returns `Location[]` serialized with
	 * `LOCATION_CALENDAR_GROUP`.
	 */
	static async listLocations() {
		return await this.get(`${this.endpoint}/locations`);
	}

	/**
	 * GET /api/v1/calendar/pilots — pilots across all calendar-visible orgs.
	 * Returns `FilteredPilotDto[]`: `{organizationId, organizationUnique,
	 * organizationName, pilotId, pilotName, pilotSurname, pilotMiddleName}`.
	 *
	 * @param {boolean} [grouped=false] When true the backend groups the list
	 *   by organization (shape change — we don't use it).
	 */
	static async listPilots(grouped = false) {
		const query = this._buildQuery({ grouped: grouped ? 1 : null });
		return await this.get(`${this.endpoint}/pilots${query}`);
	}

	/**
	 * GET /api/v1/calendar/instructors — instructors across all
	 * calendar-visible orgs. Returns `FilteredInstructorDto[]`:
	 * `{organizationId, organizationUnique, organizationName, resourceId,
	 * instructorName, instructorSurname, instructorMiddleName, locationId,
	 * locationName}`.
	 *
	 * @param {boolean} [grouped=false]
	 */
	static async listInstructors(grouped = false) {
		const query = this._buildQuery({ grouped: grouped ? 1 : null });
		return await this.get(`${this.endpoint}/instructors${query}`);
	}

	/**
	 * GET /api/v1/calendar/resources — resources (aircraft + sims) across
	 * all calendar-visible orgs. Returns `FilteredResourceDto[]`:
	 * `{organizationId, organizationUnique, organizationName, resourceId,
	 * resourceName, locationId, locationName}`.
	 *
	 * @param {boolean} [grouped=false]
	 */
	static async listResources(grouped = false) {
		const query = this._buildQuery({ grouped: grouped ? 1 : null });
		return await this.get(`${this.endpoint}/resources${query}`);
	}

	// ─── Events: list / read ──────────────────────────────────────────────────

	/**
	 * GET /api/v1/org/{org}/calendar/events/{view}
	 * Returns events for the given calendar view.
	 *
	 * @param {('month'|'week'|'day'|'dispatch'|'resource')} view
	 * @param {Object} [params] Query params matching `EventsRequestDto`
	 *   + `BaseCalendarRequestDto`. Canonical key list (all plural where
	 *   applicable, verified against the DTO reflection):
	 *     - `timezone` (string, Olson name)
	 *     - `date` (string, Y-m-d — used by month/week views as an anchor)
	 *     - `organizations` (string[] of unique slugs)
	 *     - `locations` (number[] or string[] of ids)
	 *     - `resources` (number[] or string[] of ids)
	 *     - `pilots` (number[] or string[] of ids)
	 *     - `instructors` (number[] or string[] of ids)
	 *     - `maintenance` (boolean)
	 *     - `waiting` (boolean)
	 *     - `myOnly` (boolean — note camelCase vs slice's `myonly`)
	 *     - `activityTypeColorScheme` (boolean — Day view only)
	 *     - `limit` (number, optional)
	 *     - `compact` (boolean — month/week/day: per-view lean payload; servers
	 *       that predate it ignore the flag and return the full shape)
	 *     - `days` (number, day view with `compact`: span length from `date`)
	 *   See `symphony-holdshort/src/DTO/API/Request/Calendar/BaseCalendarRequestDto.php`
	 *   + `EventsRequestDto.php` for the authoritative property list.
	 * @returns {Promise<{data: *}>} Wrapped Symfony `BaseViewResponse`.
	 */
	static async listEvents(view, params) {
		const path = this.buildOrgEndpoint(`/calendar/events/${view}`);
		return await this.get(`${path}${this._buildQuery(params)}`);
	}

	/**
	 * GET /api/v1/org/{org}/calendar/event/{id}?timezone={tz}
	 * Returns a single event with its `EVENT_READ_GROUP` payload.
	 *
	 * @param {number|string} id Event id.
	 * @param {string} [timezone='UTC'] Olson timezone name.
	 */
	static async getEvent(id, timezone = 'UTC', orgUniqueOverride = null) {
		// With multi-org selection, the event may belong to a different org
		// than the current URL-path one. Caller can pass the event's own
		// `organizationUnique` to hit the correct scoped endpoint; otherwise
		// fall back to the default `buildOrgEndpoint` (URL-path org).
		const path = orgUniqueOverride
			? `/org/${orgUniqueOverride}/calendar/event/${id}`
			: this.buildOrgEndpoint(`/calendar/event/${id}`);
		return await this.get(`${path}${this._buildQuery({ timezone })}`);
	}

	// ─── Events: Weight & Balance ────────────────────────────────────────────────

	/**
	 * GET /api/v1/org/{org}/calendar/weight-and-balance/{eventId}/
	 * Returns current W&B state for the event.
	 *
	 * @param {number|string} eventId
	 * @param {string} [orgUniqueOverride] Target org slug (multi-org events).
	 */
	static async getWeightAndBalance(eventId, orgUniqueOverride = null) {
		const path = orgUniqueOverride
			? `/org/${orgUniqueOverride}/calendar/weight-and-balance/${eventId}/`
			: this.buildOrgEndpoint(`/calendar/weight-and-balance/${eventId}/`);
		return await this.get(path);
	}

	/**
	 * POST /api/v1/org/{org}/calendar/weight-and-balance/recalculate/{eventId}/
	 * Recalculates W&B given updated rows/passengers — does not persist.
	 *
	 * @param {number|string} eventId
	 * @param {Object} payload Matches `UpdateWeightAndBalanceRequest.php`.
	 * @param {string} [orgUniqueOverride]
	 */
	static async recalculateWeightAndBalance(eventId, payload, orgUniqueOverride = null) {
		const path = orgUniqueOverride
			? `/org/${orgUniqueOverride}/calendar/weight-and-balance/recalculate/${eventId}/`
			: this.buildOrgEndpoint(`/calendar/weight-and-balance/recalculate/${eventId}/`);
		return await this.post(path, payload);
	}

	/**
	 * POST /api/v1/org/{org}/calendar/weight-and-balance/update/{eventId}/
	 * Persists W&B data for the event.
	 *
	 * @param {number|string} eventId
	 * @param {Object} payload Matches `UpdateWeightAndBalanceRequest.php`.
	 * @param {string} [orgUniqueOverride]
	 */
	static async updateWeightAndBalance(eventId, payload, orgUniqueOverride = null) {
		const path = orgUniqueOverride
			? `/org/${orgUniqueOverride}/calendar/weight-and-balance/update/${eventId}/`
			: this.buildOrgEndpoint(`/calendar/weight-and-balance/update/${eventId}/`);
		return await this.post(path, payload);
	}

	/**
	 * POST /api/v1/org/{org}/calendar/weight-and-balance/update-my/{eventId}/
	 * Persists W&B data for an event the current user owns (pilot or
	 * instructor), without requiring `weightBalanceEventManage`.
	 *
	 * @param {number|string} eventId
	 * @param {Object} payload Matches `UpdateWeightAndBalanceRequest.php`.
	 * @param {string} [orgUniqueOverride]
	 */
	static async updateMyWeightAndBalance(eventId, payload, orgUniqueOverride = null) {
		const path = orgUniqueOverride
			? `/org/${orgUniqueOverride}/calendar/weight-and-balance/update-my/${eventId}/`
			: this.buildOrgEndpoint(`/calendar/weight-and-balance/update-my/${eventId}/`);
		return await this.post(path, payload);
	}

	// ─── Events: create / update ──────────────────────────────────────────────

	/**
	 * POST /api/v1/org/{org}/calendar/event
	 * Creates an event on behalf of someone else (requires
	 * `calendarManageAnothers` access). Body matches `EventParamsRequestDto`.
	 *
	 * Multi-org: pass `orgUniqueOverride` when the form's org differs from
	 * the URL-path org, otherwise the backend looks up the form's org inside
	 * the URL-path org's scope and returns "Organization with params not found".
	 *
	 * @param {Object} payload See `EventParamsRequestDto.php`.
	 * @param {string} [orgUniqueOverride] Target org slug for the URL path.
	 */
	static async createEvent(payload, orgUniqueOverride = null) {
		const path = orgUniqueOverride
			? `/org/${orgUniqueOverride}/calendar/event`
			: this.buildOrgEndpoint('/calendar/event');
		return await this.post(`${path}${this._buildQuery({ compact: true })}`, payload);
	}

	/**
	 * POST /api/v1/org/{org}/calendar/my-event
	 * Creates an event whose owner is the current user (requires
	 * `calendarManageMy` access). Body matches `EventParamsRequestDto`.
	 *
	 * Multi-org: pass `orgUniqueOverride` when the form's chosen org differs
	 * from the URL-path org (e.g. user picks Org B from an Org A page).
	 * Without it, the URL targets Org A but the payload references Org B
	 * resources → backend rejects with "Organization with params not found".
	 *
	 * @param {Object} payload See `EventParamsRequestDto.php`.
	 * @param {string} [orgUniqueOverride] Target org slug for the URL path.
	 */
	static async createMyEvent(payload, orgUniqueOverride = null) {
		const path = orgUniqueOverride
			? `/org/${orgUniqueOverride}/calendar/my-event`
			: this.buildOrgEndpoint('/calendar/my-event');
		return await this.post(`${path}${this._buildQuery({ compact: true })}`, payload);
	}

	/**
	 * POST /api/v1/org/{org}/calendar/event/{id}
	 * Updates an event the current user does NOT own.
	 *
	 * Multi-org: pass `orgUniqueOverride` when the event belongs to a
	 * different org than the URL-path one (legacy parity with multi-org
	 * Create Event modal). Mirrors `getEvent` pattern at `:163-171`.
	 *
	 * @param {number|string} id Event id.
	 * @param {Object} payload See `EventParamsRequestDto.php`.
	 * @param {string} [orgUniqueOverride] Target org slug for the URL path.
	 */
	static async updateEvent(id, payload, orgUniqueOverride = null) {
		const path = orgUniqueOverride
			? `/org/${orgUniqueOverride}/calendar/event/${id}`
			: this.buildOrgEndpoint(`/calendar/event/${id}`);
		return await this.post(`${path}${this._buildQuery({ compact: true })}`, payload);
	}

	/**
	 * POST /api/v1/org/{org}/calendar/my-event/{id}
	 * Updates an event the current user owns.
	 *
	 * @param {number|string} id Event id.
	 * @param {Object} payload See `EventParamsRequestDto.php`.
	 * @param {string} [orgUniqueOverride] Target org slug for the URL path.
	 */
	static async updateMyEvent(id, payload, orgUniqueOverride = null) {
		const path = orgUniqueOverride
			? `/org/${orgUniqueOverride}/calendar/my-event/${id}`
			: this.buildOrgEndpoint(`/calendar/my-event/${id}`);
		return await this.post(`${path}${this._buildQuery({ compact: true })}`, payload);
	}

	// ─── Events: drag-and-drop move ───────────────────────────────────────────

	/**
	 * POST /api/v1/org/{org}/calendar/event/{id}/move
	 * Moves an event the current user does NOT own (DnD on the calendar grid).
	 *
	 * Multi-org: pass `orgUniqueOverride` when the event belongs to a
	 * different org than the URL-path one. Mirrors `getEvent` pattern at `:163-171`.
	 *
	 * @param {number|string} id Event id.
	 * @param {Object} payload See `EventParamsRequestDto.php` (move fields).
	 * @param {string} [orgUniqueOverride] Target org slug for the URL path.
	 */
	static async moveEvent(id, payload, orgUniqueOverride = null) {
		const path = orgUniqueOverride
			? `/org/${orgUniqueOverride}/calendar/event/${id}/move`
			: this.buildOrgEndpoint(`/calendar/event/${id}/move`);
		return await this.post(path, payload);
	}

	/**
	 * POST /api/v1/org/{org}/calendar/my-event/{id}/move
	 * Moves an event the current user owns.
	 *
	 * @param {number|string} id Event id.
	 * @param {Object} payload See `EventParamsRequestDto.php` (move fields).
	 * @param {string} [orgUniqueOverride] Target org slug for the URL path.
	 */
	static async moveMyEvent(id, payload, orgUniqueOverride = null) {
		const path = orgUniqueOverride
			? `/org/${orgUniqueOverride}/calendar/my-event/${id}/move`
			: this.buildOrgEndpoint(`/calendar/my-event/${id}/move`);
		return await this.post(path, payload);
	}

	// ─── Events: delete / cancel ──────────────────────────────────────────────

	/**
	 * DELETE /api/v1/org/{org}/calendar/event/{id}?eventsForAction=…&timezone=…
	 * Deletes a single event or a group of recurring events.
	 *
	 * Multi-org: pass `orgUniqueOverride` when the event belongs to a
	 * different org than the URL-path one. Mirrors `getEvent` pattern.
	 *
	 * @param {number|string} id Event id.
	 * @param {Object} [params]
	 * @param {string} [params.eventsForAction] One of `current`, `following`,
	 *   `not-started`, `all`. Omitting it on a recurring event means "this
	 *   occurrence only" (backend default — used by the without-permission
	 *   trap-protection path). NOTE: legacy AngularJS used `single` for the
	 *   single-occurrence value; the Symfony DTO uses `current` (verified
	 *   against `EventsForActionEnum`).
	 * @param {string} [params.timezone='UTC'] Olson timezone name.
	 * @param {string} [orgUniqueOverride] Target org slug for the URL path.
	 */
	static async deleteEvent(id, params, orgUniqueOverride = null) {
		const path = orgUniqueOverride
			? `/org/${orgUniqueOverride}/calendar/event/${id}`
			: this.buildOrgEndpoint(`/calendar/event/${id}`);
		return await this.delete(`${path}${this._buildQuery(params)}`);
	}

	/**
	 * POST /api/v1/org/{org}/calendar/event/{id}/cancel
	 * Cancels (with reason) an event the current user does NOT own.
	 *
	 * Multi-org: pass `orgUniqueOverride` when the event belongs to a
	 * different org than the URL-path one. Same pattern as `getEvent`/
	 * `updateEvent`.
	 *
	 * @param {number|string} id Event id.
	 * @param {Object} payload See `CancelEventRequestDto.php` — `{ reason }`.
	 * @param {Object} [params] Optional query params: `{ eventsForAction, timezone }`.
	 *   `eventsForAction` is required for recurring cancels (omitting triggers the
	 *   backend single-occurrence default — same trap as delete).
	 * @param {string} [orgUniqueOverride] Target org slug for the URL path.
	 */
	static async cancelEvent(id, payload, params = null, orgUniqueOverride = null) {
		const path = orgUniqueOverride
			? `/org/${orgUniqueOverride}/calendar/event/${id}/cancel`
			: this.buildOrgEndpoint(`/calendar/event/${id}/cancel`);
		return await this.post(`${path}${this._buildQuery(params)}`, payload);
	}

	/**
	 * POST /api/v1/org/{org}/calendar/event/{id}/cancel-my
	 * Cancels (with reason) an event the current user owns.
	 * NOTE: Symfony route is `/calendar/event/{id}/cancel-my`, not
	 * `/calendar/my-event/{id}/cancel` — verified in
	 * `CalendarController.php:1232`.
	 *
	 * @param {number|string} id Event id.
	 * @param {Object} payload See `CancelEventRequestDto.php` — `{ reason }`.
	 * @param {Object} [params] Optional query params: `{ eventsForAction, timezone }`.
	 * @param {string} [orgUniqueOverride] Target org slug for the URL path.
	 */
	static async cancelMyEvent(id, payload, params = null, orgUniqueOverride = null) {
		const path = orgUniqueOverride
			? `/org/${orgUniqueOverride}/calendar/event/${id}/cancel-my`
			: this.buildOrgEndpoint(`/calendar/event/${id}/cancel-my`);
		return await this.post(`${path}${this._buildQuery(params)}`, payload);
	}

	// ─── Events: validation ───────────────────────────────────────────────────

	/**
	 * POST /api/v1/org/{org}/calendar/check-event-params
	 * POST /api/v1/org/{org}/calendar/check-event-params/{id}  (when `id` set)
	 *
	 * Validates an event payload before save (conflict detection, business
	 * rules). Body matches `EventParamsRequestDto`.
	 *
	 * Multi-org: pass `orgUniqueOverride` when the form's org differs from
	 * the URL-path org. Same reason as `createEvent`.
	 *
	 * @param {Object} payload See `EventParamsRequestDto.php`.
	 * @param {number|string} [id] Optional existing event id (skip-self in conflicts).
	 * @param {string} [orgUniqueOverride] Target org slug for the URL path.
	 */
	static async validateEventParams(payload, id, orgUniqueOverride = null) {
		const base = orgUniqueOverride ? `/org/${orgUniqueOverride}` : this.buildOrgEndpoint('');
		const path = id
			? `${base}/calendar/check-event-params/${id}`
			: `${base}/calendar/check-event-params`;
		return await this.post(`${path}${this._buildQuery({ compact: true })}`, payload);
	}

	// ─── Check-in / Check-out flow ────────────────────────────────────────────

	/**
	 * GET /api/v1/org/{org}/calendar/{id}/check-in-start?timezone={tz}
	 * Returns initial data for the check-in modal (current meter values, etc.).
	 *
	 * @param {number|string} id Event id.
	 * @param {string} [timezone='UTC'] Olson timezone name.
	 */
	static async checkInStart(id, timezone = 'UTC', orgUniqueOverride = null) {
		const path = orgUniqueOverride
			? `/org/${orgUniqueOverride}/calendar/${id}/check-in-start`
			: this.buildOrgEndpoint(`/calendar/${id}/check-in-start`);
		return await this.get(`${path}${this._buildQuery({ timezone })}`);
	}

	/**
	 * POST /api/v1/org/{org}/calendar/{id}/check-in-finish?timezone={tz}
	 * Submits the check-in form (actual arrival, end meter, fuel, etc.).
	 *
	 * @param {number|string} id Event id.
	 * @param {Object} payload See `CheckInFinishRequestDto.php`. Notable:
	 *   `comment` is SINGULAR (legacy `comments` plural must be renamed via
	 *   `toCheckInFinishPayload` in `checkInOutAdapters.js` before reaching
	 *   this method).
	 * @param {string} [timezone='UTC'] Olson timezone name.
	 */
	static async checkInFinish(id, payload, timezone = 'UTC', orgUniqueOverride = null) {
		const path = orgUniqueOverride
			? `/org/${orgUniqueOverride}/calendar/${id}/check-in-finish`
			: this.buildOrgEndpoint(`/calendar/${id}/check-in-finish`);
		return await this.post(`${path}${this._buildQuery({ timezone })}`, payload);
	}

	/**
	 * POST /api/v1/org/{org}/calendar/{id}/check-in-cancel?timezone={tz}
	 * Cancels an in-progress check-in (clears the user's checked-in state).
	 *
	 * @param {number|string} id Event id.
	 * @param {string} [timezone='UTC'] Olson timezone name.
	 */
	static async cancelCheckIn(id, timezone = 'UTC', orgUniqueOverride = null) {
		const path = orgUniqueOverride
			? `/org/${orgUniqueOverride}/calendar/${id}/check-in-cancel`
			: this.buildOrgEndpoint(`/calendar/${id}/check-in-cancel`);
		return await this.post(`${path}${this._buildQuery({ timezone })}`, {});
	}

	/**
	 * GET /api/v1/org/{org}/calendar/{id}/check-out-start?timezone={tz}
	 * Returns initial data for the check-out modal.
	 *
	 * @param {number|string} id Event id.
	 * @param {string} [timezone='UTC'] Olson timezone name.
	 */
	static async checkOutStart(id, timezone = 'UTC', orgUniqueOverride = null) {
		const path = orgUniqueOverride
			? `/org/${orgUniqueOverride}/calendar/${id}/check-out-start`
			: this.buildOrgEndpoint(`/calendar/${id}/check-out-start`);
		return await this.get(`${path}${this._buildQuery({ timezone })}`);
	}

	/**
	 * POST /api/v1/org/{org}/calendar/{id}/check-out-finish?timezone={tz}
	 * Submits the check-out form (flight time, departure / expected arrival).
	 *
	 * @param {number|string} id Event id.
	 * @param {Object} payload See `CheckOutInFinishRequestDto.php`
	 *   (`flightTime`, `departureTime`, `expectedArrivalTime` — all optional).
	 * @param {string} [timezone='UTC'] Olson timezone name.
	 */
	static async checkOutFinish(id, payload, timezone = 'UTC', orgUniqueOverride = null) {
		const path = orgUniqueOverride
			? `/org/${orgUniqueOverride}/calendar/${id}/check-out-finish`
			: this.buildOrgEndpoint(`/calendar/${id}/check-out-finish`);
		return await this.post(`${path}${this._buildQuery({ timezone })}`, payload);
	}

	/**
	 * POST /api/v1/org/{org}/calendar/{id}/check-out-cancel?timezone={tz}
	 * Cancels an in-progress check-out (releases the resource).
	 *
	 * @param {number|string} id Event id.
	 * @param {string} [timezone='UTC'] Olson timezone name.
	 */
	static async cancelCheckOut(id, timezone = 'UTC', orgUniqueOverride = null) {
		const path = orgUniqueOverride
			? `/org/${orgUniqueOverride}/calendar/${id}/check-out-cancel`
			: this.buildOrgEndpoint(`/calendar/${id}/check-out-cancel`);
		return await this.post(`${path}${this._buildQuery({ timezone })}`, {});
	}

	// Sub 10 (2026-05-15): `pastCheckOutCheckIn` REMOVED along with the
	// combined past-event flow. Past events use the standard `checkOutFinish`
	// + `checkInFinish` calls in two separate user actions, matching the
	// legacy UX. Symfony route stays as dead contract per user policy.

	// ─── Legacy hangars endpoints — Meters Start inline edit on Check Out ─────
	// No Symfony equivalent yet. Backend ask R'' filed in parent epic Phase 2.1
	// for long-term `/calendar/{id}/counter-save` (or resources counterpart).
	// Until then, Calendar Month V2 Check Out screen calls these legacy PHP
	// routes directly. Wrap them here so the Month V2 components don't have
	// to know about the org URL shape.

	/**
	 * POST /org/{org}/hangars/saveCounter/{aircraftId}/ — legacy PHP route.
	 * Updates an aircraft's current counter reading.
	 *
	 * @param {number|string} aircraftId
	 * @param {Object} payload  `{ counter: <number>, comments?: <string> }` per legacy form.
	 * @param {string} [orgUniqueOverride]
	 */
	static async saveAircraftCounter(aircraftId, payload, orgUniqueOverride = null) {
		const base = orgUniqueOverride ? `/org/${orgUniqueOverride}` : `/org/${this._currentOrgUnique()}`;
		return await this.post(`${base}/hangars/saveCounter/${aircraftId}/`, payload);
	}

	/**
	 * POST /org/{org}/hangars/saveSimulatorCounter/{simulatorId}/ — legacy PHP route.
	 * Updates a simulator's current counter reading.
	 *
	 * @param {number|string} simulatorId
	 * @param {Object} payload  `{ counter: <number>, comments?: <string> }`.
	 * @param {string} [orgUniqueOverride]
	 */
	static async saveSimulatorCounter(simulatorId, payload, orgUniqueOverride = null) {
		const base = orgUniqueOverride ? `/org/${orgUniqueOverride}` : `/org/${this._currentOrgUnique()}`;
		return await this.post(`${base}/hangars/saveSimulatorCounter/${simulatorId}/`, payload);
	}

	/**
	 * Resolve the current org unique from the request context the same way
	 * `buildOrgEndpoint` does. Pulled out so the legacy hangars helpers can
	 * mirror that resolution without duplicating env lookups.
	 */
	static _currentOrgUnique() {
		// `buildOrgEndpoint('/x')` returns `/org/<unique>/x`; extract by
		// stripping the known suffix. This stays in lock-step with whatever
		// `BaseApi.buildOrgEndpoint` reads from (window.serverData,
		// localStorage, etc.) without re-implementing it.
		const sentinel = this.buildOrgEndpoint('/__org__');
		const match = sentinel.match(/^\/org\/([^/]+)\/__org__$/);
		return match ? match[1] : '';
	}
}

export default CalendarApi;
